import { AxiosInstance } from 'axios'

import { User } from '@percent/cause-dashboard/context/auth/authContext/AuthContext.types'
import { apiPaths } from '@percent/cause-dashboard/services/apiPaths'
import { PBPResponse } from '@percent/cause-dashboard/services/types/pbpResponses.types'
import { LoginType, OptKeyConfirmResponse, OtpKeyResponse } from './auth.types'

type AuthServiceCapabilities = {
  percentClient: AxiosInstance
}

export const authService = ({ percentClient }: AuthServiceCapabilities) => ({
  login: (
    loginDetails: { email: string; password: string; enforce?: boolean },
    captchaHeaders: Record<string, string> = {}
  ) => percentClient.post(`${apiPaths.v1Auth}login`, { ...loginDetails }, { headers: captchaHeaders }),
  forgotPassword: (email: string, captchaHeaders: Record<string, string> = {}) =>
    percentClient.post(
      `${apiPaths.v1Auth}forgot-password`,
      { accountType: 'cause', email: email.trim() },
      { headers: captchaHeaders }
    ),
  logout: () => percentClient.post(`${apiPaths.v1Auth}logout`),
  setPassword: ({ password, hash, otpCode }: { hash: string; password: string; otpCode?: string }) =>
    percentClient.post(`${apiPaths.v1Auth}reset-password`, { password, hash, ...(otpCode ? { otpCode } : {}) }),
  verifyAccount: (hash: string) => percentClient.post(`${apiPaths.v1Auth}verify`, { hash }),
  verifyAccountRequest: () => percentClient.post(`${apiPaths.v1Auth}verify-request`, { accountType: 'cause' }),
  signUp: ({
    email,
    password,
    fullName,
    phoneNumber,
    preferredLanguage,
    token,
    organisationDetails,
    organisationId,
    marketingConsent,
    initialVsId,
    signUpAttribution
  }: LoginType) =>
    percentClient.post(`${apiPaths.v1Auth}signup`, {
      email: email.trim(),
      password,
      accountType: 'cause',
      fullName,
      phoneNumber,
      preferredLanguage,
      // Same contract as add-organisation, request-to-join and store applications: a `token` body
      // field verified by IAM. Omitted rather than sent as null when the captcha flag is off.
      token: token ?? undefined,
      organisationId,
      marketingConsent,
      initialVsId,
      signUpAttribution,
      ...organisationDetails
    }),
  confirmOtpKey: ({ otpCode }: { otpCode: string }) =>
    percentClient.post<PBPResponse<OptKeyConfirmResponse>>('v1/account/otp-key-confirm', { otpCode }),
  generateQRCode: ({ password }: { password: string }) =>
    percentClient.post<OtpKeyResponse>('/v1/account/otp-key', { password }),
  verifyOtpKey: ({ token }: { token: string }) => percentClient.post('v1/auth/otp-verify', { token }),
  loginWithRecoveryCode: ({ backupCode }: { backupCode: string }) =>
    percentClient.post<PBPResponse<User>>('/v1/auth/recovery', { backupCode }),
  disable2fa: ({ password }: { password: string }) =>
    percentClient.post<OtpKeyResponse>('/v1/account/otp-key-remove', { password })
})
